import * as FC from "@fluentui/react-components";
import { useContext, useEffect, useState } from "react";
import { Context } from "../context";
import { idString } from "../db_utils";
import { TableDef } from "../table_def";

export const ModelLeafDef = new TableDef("model_leaf", {
    seq: {
        label: "编号",
    },
    length: {
        label: "长度(米)",
        display: (item) => item.length?.toString(),
    },
    manu: {
        label: "厂商",
        display: (item) => item.manu?.name,
    },
    description: {
        label: "备注",
    },
});

const model_leaf_searchable_columns = [
    "seq",
    "manu.name",
    "length",
    // "description"
];

const applyModelLeafFilter = (items, filter) => {
    if (!filter || typeof filter !== "string" || !filter.trim()) {
        return items;
    }

    const filterLower = filter.toLowerCase();
    return items.filter((item) =>
        model_leaf_searchable_columns.some((column) => {
            if (column === "manu.name") {
                return item.manu?.name?.toLowerCase().includes(filterLower);
            }
            return item[column]?.toString().toLowerCase().includes(filterLower);
        })
    );
};

/**
 * Loads blade models, propagating transport/auth failures to the caller.
 *
 * @param options.fieldId when set, only models belonging to that field are returned. Like
 *                        turbine models, a blade model is owned by exactly one field
 *                        (ModelLeaf.FieldItemId) and the blade write path rejects a
 *                        cross-field pair with LeafRelationFieldMismatch — and it demands
 *                        the model's MANUFACTURER live in the same field too.
 */
export const fetchModelLeaves = async (
    apiClient,
    { filter = null, fieldId = null } = {}
) => {
    const params = {};
    if (fieldId !== null && fieldId !== undefined && fieldId !== "") {
        params.fieldId = fieldId;
    }

    const items = await apiClient.getModelLeaves(params);
    return applyModelLeafFilter(items || [], filter);
};

/** Failure-swallowing wrapper for the catalogue grid. See fetchModelLeaves for pickers. */
export const listModelLeaf = async (apiClient, filter = null, fieldId = null) => {
    try {
        return await fetchModelLeaves(apiClient, { filter, fieldId });
    } catch (error) {
        console.error("Failed to load model leaves:", error);
        if (error.message && error.message.includes('404')) {
            console.warn("Model Leaf API not implemented in backend. Using empty list.");
        }
        return [];
    }
};

/** See modelTurbineOptionText: the suffix is added only when the code omits the maker. */
export const modelLeafOptionText = (item) => {
    const seq = `${item?.seq ?? ""}`;
    const manu = item?.manu?.name;
    if (manu && seq.includes(manu)) {
        return seq;
    }

    const details = [manu, item?.length ? `${item.length}m` : null].filter(Boolean);
    return details.length > 0 ? `${seq} (${details.join(" · ")})` : seq;
};

/**
 * Blade-model picker. Models are a global catalogue, so a fieldId supplied by an older
 * caller is deliberately ignored.
 *
 * @param {import("@fluentui/react-components").DropdownProps} props
 */
export const ModelLeafDropDown = ({ fieldId: _legacyFieldId, placeholder, ...props }) => {
    void _legacyFieldId;
    const { apiClient } = useContext(Context);
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        if (!apiClient) {
            return undefined;
        }

        let cancelled = false;
        setStatus("loading");
        fetchModelLeaves(apiClient)
            .then((models) => {
                if (cancelled) {
                    return;
                }
                setItems(models);
                setStatus("ready");
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                console.error("Failed to load model leaves:", error);
                setItems([]);
                setStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [apiClient]);

    const isEmpty = status === "ready" && items.length === 0;
    const resolvedPlaceholder =
        status === "loading"
            ? "正在加载叶片型号..."
            : status === "error"
            ? "叶片型号加载失败"
            : isEmpty
            ? "暂无叶片型号"
            : placeholder ?? "请选择叶片型号";

    return (
        <>
            <FC.Dropdown
                placeholder={resolvedPlaceholder}
                disabled={status !== "ready" || isEmpty}
                {...props}
            >
                {items.map((item) => (
                    <FC.Option
                        key={idString(item.id)}
                        value={idString(item.id)}
                        text={modelLeafOptionText(item)}
                    >
                        {modelLeafOptionText(item)}
                    </FC.Option>
                ))}
            </FC.Dropdown>
            {isEmpty && (
                <FC.Text size={200} style={{ color: FC.tokens.colorPaletteRedForeground1 }}>
                    系统中还没有叶片型号，请先在「叶片型号」中创建。
                </FC.Text>
            )}
            {status === "error" && (
                <FC.Text size={200} style={{ color: FC.tokens.colorPaletteRedForeground1 }}>
                    叶片型号列表加载失败，请刷新页面重试；请勿在确认前重复创建型号。
                </FC.Text>
            )}
        </>
    );
};

export const AttachList = ["A", "B", "C"];
/**
 * @param {import("@fluentui/react-components").DropdownProps} props
 */
export const AttachDropdown = (props) => {
    return (
        <FC.Dropdown {...props}>
            {AttachList.map((it) => (
                <FC.Option key={it} value={it}>
                    {it.toString()}
                </FC.Option>
            ))}
        </FC.Dropdown>
    );
};
